import { useEffect, useState } from 'react';
import { mapsApi } from '../../api/maps';

/**
 * The road between two pins from InGo's engine (`POST routes`), asked again
 * only when a pin moves.
 *
 * - `polyline`: the encoded road for `InGoMap`, or null until it answers,
 *   when routing is off, or when it is not wanted (`enabled` false). The last
 *   road stays drawn while the next is asked, so the map does not blink.
 * - `distanceKm`: the road's length, only for the pins asked now: null while
 *   they are being answered, so a figure never belongs to pins already moved
 *   (Rio, 5 Oct 2026, the delivery screen's distance by road: "ok go on").
 *
 * @param {{ lat: number, lng: number } | null} from
 * @param {{ lat: number, lng: number } | null} to
 * @param {boolean} [enabled]
 * @returns {{ polyline: string | null, distanceKm: number | null }}
 */
export function useInGoRoute(from, to, enabled = true) {
  const [road, setRoad] = useState({ key: '', polyline: null, distanceKm: null });
  const key = enabled && from && to ? `${from.lat},${from.lng};${to.lat},${to.lng}` : '';

  useEffect(() => {
    if (!key) {
      setRoad({ key: '', polyline: null, distanceKm: null });
      return undefined;
    }
    let gone = false;
    const [a, b] = key.split(';').map((pair) => pair.split(',').map(Number));
    mapsApi.route([{ lat: a[0], lng: a[1] }, { lat: b[0], lng: b[1] }]).then(
      (route) => {
        if (gone) return;
        const km = Number(route?.distance_km);
        setRoad({ key, polyline: route?.polyline ?? null, distanceKm: route && Number.isFinite(km) && km > 0 ? km : null });
      },
      () => !gone && setRoad({ key, polyline: null, distanceKm: null }),
    );

    return () => {
      gone = true;
    };
  }, [key]);

  return { polyline: road.polyline, distanceKm: key && road.key === key ? road.distanceKm : null };
}

/**
 * The road between two pins as an encoded polyline for `InGoMap` (the
 * rider's maps); `useInGoRoute` for its length too.
 *
 * @param {{ lat: number, lng: number } | null} from
 * @param {{ lat: number, lng: number } | null} to
 * @param {boolean} [enabled]
 */
export function useInGoRoad(from, to, enabled = true) {
  return useInGoRoute(from, to, enabled).polyline;
}
